import { useApolloClient } from "@apollo/client";
import { Button, Card, NonIdealState, Spinner } from "@blueprintjs/core";
import { useGetEvents } from "@package/ui-apollo-components";
import {
  EventPayloadType,
  type GetEventsQueryVariables,
} from "@package/ui-gql/index-analytics";
import {
  useGetLocationQuery,
  useGetProfileQuery,
} from "@package/ui-gql/index-data-access";
import {
  AutoZoomFrame,
  getWallDimensions,
  Wall,
  WallContextProvider,
} from "@package/ui-wall";
import { useMemo, useState } from "react";
import { WALL_HEIGHT_PREVIEW_AVAILABLE } from "../../constants/contants";
import { getLastSevenDaysRange } from "../../utils/analytics-format-utils";
import { WallPreviewContainer } from "../WallPreviewContainer/WallPreviewContainer";
import { useAppStore } from "./../../store/store";
import { HEATMAP_RAMP_HEX } from "./heatmap-color-ramp";
import { WallPreviewHeatmap } from "./WallPreviewHeatmap";

const HEATMAP_EVENT_TYPES = [
  EventPayloadType.PostActioned,
  EventPayloadType.PostInteracted,
  EventPayloadType.PostTimeExtended,
  EventPayloadType.RestingMediaInteracted,
];

type WallPreviewLocationProps = {
  location?: string;
  version?: number | null;
};

export function WallPreviewLocation({
  location,
  version,
}: WallPreviewLocationProps) {
  const client = useApolloClient();
  const store = useAppStore();
  const [showHeatmap, setShowHeatmap] = useState(false);

  const {
    data: dataLocation,
    loading: loadingLocation,
    error: errorLocation,
  } = useGetLocationQuery({
    variables: {
      id: location ?? "",
    },
    skip: !location,
  });
  const {
    data: dataProfile,
    loading: loadingProfile,
    error: errorProfile,
  } = useGetProfileQuery({
    variables: {
      id: dataLocation?.item?.profile ?? "",
    },
    skip: !dataLocation?.item?.profile,
  });

  const range = useMemo(() => getLastSevenDaysRange(), []);
  const variablesHeatmap = useMemo<GetEventsQueryVariables>(
    () => ({
      filter: {
        eventType: HEATMAP_EVENT_TYPES,
        location: location ? [location] : [],
        range,
      },
      page: 0,
      perPage: 2000,
    }),
    [location, range],
  );
  const { data: dataHeatmap, loading: loadingHeatmap } = useGetEvents(
    variablesHeatmap,
    !showHeatmap || !location,
  );

  const screen = dataProfile?.item?.screen;
  const screenType = dataProfile?.item?.type ?? undefined;
  const columns = screen?.columns ?? 1;
  const rows = screen?.rows ?? 1;
  const wallDimensions = screen
    ? getWallDimensions(screenType, screen)
    : undefined;
  // Derive per-screen tile size from the composite wall size rather than
  // getScreenDimensions() — for ScreenType.Custom, `screen.size` IS the whole
  // composite already (not a single tile), so dividing by columns/rows here is the
  // one derivation that's correct for both Tv (repeated single-resolution tiles)
  // and Custom (an explicit total canvas size) wall types.
  const tileDimensions = wallDimensions
    ? {
        width: wallDimensions.width / columns,
        height: wallDimensions.height / rows,
      }
    : undefined;

  const heatmapPoints = useMemo(() => {
    if (!tileDimensions) {
      return [];
    }

    const points: { x: number; y: number }[] = [];

    for (const event of dataHeatmap?.items ?? []) {
      for (const entry of event?.payload ?? []) {
        if (entry && "position" in entry && entry.position) {
          const screenIndex =
            "screen" in entry && typeof entry.screen === "number"
              ? entry.screen
              : 0;

          points.push({
            x:
              (screenIndex % columns) * tileDimensions.width + entry.position.x,
            y:
              Math.floor(screenIndex / columns) * tileDimensions.height +
              entry.position.y,
          });
        }
      }
    }

    return points;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataHeatmap?.items,
    tileDimensions?.width,
    tileDimensions?.height,
    columns,
  ]);

  if (!location) {
    return (
      <WallPreviewContainer>
        <NonIdealState
          className="min-h-36"
          icon="info-sign"
          title="Select a Location to Preview"
        />
      </WallPreviewContainer>
    );
  }

  if (loadingLocation || loadingProfile) {
    return (
      <WallPreviewContainer>
        <Spinner className="min-h-36" />
      </WallPreviewContainer>
    );
  }

  if (
    errorLocation ||
    errorProfile ||
    !dataProfile?.item?.id ||
    !dataProfile?.item?.type ||
    !dataProfile?.item?.screen
  ) {
    return (
      <WallPreviewContainer>
        <NonIdealState
          className="min-h-36"
          icon="error"
          title="Location Canvas Misconfigured"
          description="Please check the selected canvas's screen configuration."
        />
      </WallPreviewContainer>
    );
  }

  return (
    <Card compact elevation={1} className="relative">
      <Button
        className="absolute top-2 right-2 z-10"
        icon="heatmap"
        active={showHeatmap}
        intent={showHeatmap ? "primary" : "none"}
        minimal={!showHeatmap}
        small
        text="Heatmap"
        onClick={() => setShowHeatmap((prev) => !prev)}
      />
      <WallPreviewContainer key={version}>
        <AutoZoomFrame
          type={dataProfile.item.type}
          profileScreen={dataProfile.item.screen}
          height={WALL_HEIGHT_PREVIEW_AVAILABLE}
        >
          <WallContextProvider
            clientDataAccess={client}
            location={location}
            profile={dataProfile.item.id}
            store={store}
            type="local"
            caching={false}
          >
            <Wall />
          </WallContextProvider>
          {showHeatmap && (
            <>
              <div className="absolute inset-0 bg-black/50" />
              {wallDimensions && (
                <WallPreviewHeatmap
                  points={heatmapPoints}
                  width={wallDimensions.width}
                  height={wallDimensions.height}
                />
              )}
              {!loadingHeatmap && heatmapPoints.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center px-6">
                  <p className="text-xs text-white text-center">
                    No interaction data recorded for this location in the last 7
                    days.
                  </p>
                </div>
              )}
            </>
          )}
        </AutoZoomFrame>
      </WallPreviewContainer>
      {showHeatmap && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className="text-[10px] text-brand-muted">Fewer touches</span>
          <div
            className="h-2 w-24 rounded-full"
            style={{
              background: `linear-gradient(to right, ${HEATMAP_RAMP_HEX.join(", ")})`,
            }}
          />
          <span className="text-[10px] text-brand-muted">More touches</span>
        </div>
      )}
    </Card>
  );
}
